import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { useCart } from "@context/CartContext";

export default function Schedule() {
  const router = useRouter();
  const { addToCart } = useCart();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await fetch("/api/schedules");
      const result = await response.json();

      if (result.success) {
        setSchedules(result.data);
      } else {
        setError("Failed to load schedules");
      }
    } catch (err) {
      console.error("Error fetching schedules:", err);
      setError("Failed to load schedules. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = (schedule) => {
    addToCart({
      programId: schedule.programId,
      formatId: schedule.formatId,
      title: `${schedule.programTitle} - ${schedule.formatName}`,
      price: schedule.formatPrice,
      date: schedule.date,
      dateStr: schedule.dateStr,
    });
    router.push("/checkout");
  };

  return (
    <>
      <Head>
        <title>Schedule | L.I.S.T Learning Pathways</title>
        <meta
          name="description"
          content="View our upcoming leadership development workshops and program start dates."
        />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Header */}
        <section className="bg-brand-dark pt-20 pb-16 lg:pt-32 lg:pb-20 relative overflow-hidden">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-xs font-bold rounded-full mb-4">
              Upcoming Sessions
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
              Program <span className="text-brand-primary">Schedule</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed">
              Plan your leadership transformation. Find the dates that work for
              you and your team.
            </p>
          </div>
        </section>

        {/* Schedule List */}
        <section className="py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={fetchSchedules}
                  className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md hover:shadow-lg hover:shadow-brand-primary/30 transition-all"
                >
                  Try Again
                </button>
              </div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-20 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-bold text-brand-dark mb-2">
                  No Upcoming Sessions
                </h3>
                <p className="text-gray-500 mb-6">
                  Check back soon for new program dates or join our waiting
                  list.
                </p>
                <Link
                  href="/contact"
                  className="inline-block px-6 py-3 bg-brand-primary text-white font-bold rounded-md hover:shadow-lg hover:shadow-brand-primary/30 transition-all"
                >
                  Join Waiting List
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="flex flex-col lg:flex-row items-center bg-white border border-gray-100 rounded-md overflow-hidden group hover:shadow-xl hover:border-brand-primary/20 transition-all duration-300"
                  >
                    {/* Date Block */}
                    <div className="w-full lg:w-32 h-24 lg:h-28 bg-brand-dark flex flex-col items-center justify-center text-white shrink-0 group-hover:bg-brand-primary transition-colors">
                      <span className="text-sm font-bold text-gray-300 group-hover:text-white/70">
                        {schedule.month}
                      </span>
                      <span className="text-3xl font-black tabular-nums">
                        {schedule.day}
                      </span>
                    </div>

                    {/* Details Block */}
                    <div className="p-5 flex flex-col lg:flex-row flex-grow items-center justify-between text-center lg:text-left gap-4">
                      <div className="flex-1">
                        <span className="text-sm font-bold text-brand-primary mb-1 block">
                          {schedule.type}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {schedule.title}
                        </h3>
                        <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            {schedule.time}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            {schedule.location}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-center lg:items-end gap-3">
                        <div className="text-center lg:text-right">
                          <span className="text-xs text-gray-300 block">
                            Tuition
                          </span>
                          <span
                            className={`text-lg font-black ${schedule.isContactForPrice ? "text-gray-600" : "text-gray-900"}`}
                          >
                            {schedule.price}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Custom Date Request */}
        <section className="pb-16 lg:pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-gray-50 rounded-md p-6 lg:p-10 text-center max-w-3xl mx-auto">
              <h3 className="text-xl font-bold text-brand-dark mb-4">
                Looking for a Different Date?
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                We regularly add new cohorts based on demand. If you have a
                specific timeline or want to be notified of future dates, let us
                know.
              </p>
              <Link
                href="/contact"
                className="inline-block px-8 py-3 border-2 border-brand-dark text-brand-dark font-bold rounded-md hover:bg-brand-dark hover:text-white transition-all text-sm"
              >
                Join the Waiting List
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
